// src/pages/ProgettiInCorso.js
import React from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  SimpleGrid,
  Button,
  VStack,
  HStack,
  Icon,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  Image,
} from '@chakra-ui/react';
import { FaExternalLinkAlt, FaUsers, FaYoutube, FaTimes } from 'react-icons/fa';
import Helmet from 'react-helmet';

const ProgettiInCorso = () => {
  const [progetti, setProgetti] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [selectedProject, setSelectedProject] = React.useState(null);
  const [currentImageIndex, setCurrentImageIndex] = React.useState(0);
  const { isOpen, onOpen, onClose } = useDisclosure();

  // Fetch progetti da Strapi
  React.useEffect(() => {
    const fetchProgetti = async () => {
      try {
        setLoading(true);
        // Passa i filtri nella query per filtrare lato server
        const params = new URLSearchParams({
          'filters[clientSide][$eq]': 'true',
          'filters[inCorso][$eq]': 'true',
        });
        const response = await fetch(`/api/projects?${params.toString()}`);
        if (!response.ok) {
          throw new Error('Errore nel recupero dei progetti');
        }
        const data = await response.json();
        console.log('Projects data received:', data);
        if (data.projects && data.projects.length > 0) {
          console.log('First project structure:', data.projects[0]);
          console.log('First project thumbnail:', data.projects[0].thumbnail);
          console.log('First project gallery:', data.projects[0].gallery);
        }
        // I progetti sono già filtrati lato server, quindi li usiamo direttamente
        setProgetti(data.projects || []);
      } catch (err) {
        console.error('Error fetching projects:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchProgetti();
  }, []);

  // Funzioni per gestire il modale
  const handleProjectClick = (project) => {
    setSelectedProject(project);
    setCurrentImageIndex(0); // Reset dell'indice immagine quando si apre un nuovo progetto
    onOpen();
  };

  // Funzione per generare chiavi uniche per le immagini della gallery
  const generateImageKey = (projectId, image, index) => {
    if (image?.url) {
      // Usa l'URL dell'immagine se disponibile
      return `gallery-${projectId}-${image.url.split('/').pop().split('?')[0]}`;
    }
    if (image) {
      // Usa l'immagine stessa se è una stringa
      return `gallery-${projectId}-${image.split('/').pop().split('?')[0]}`;
    }
    // Fallback con indice solo se necessario
    return `gallery-${projectId}-fallback-${index}`;
  };

  // Funzioni per navigare la gallery
  const nextImage = () => {
    if (selectedProject && selectedProject.gallery) {
      setCurrentImageIndex((prevIndex) =>
        prevIndex === selectedProject.gallery.length - 1 ? 0 : prevIndex + 1
      );
    }
  };

  const prevImage = () => {
    if (selectedProject && selectedProject.gallery) {
      setCurrentImageIndex((prevIndex) =>
        prevIndex === 0 ? selectedProject.gallery.length - 1 : prevIndex - 1
      );
    }
  };

  const goToImage = (index) => {
    setCurrentImageIndex(index);
  };

  const closeModal = () => {
    onClose();
    setSelectedProject(null);
    setCurrentImageIndex(0);
  };

  // Helper per ottenere l'URL dell'immagine per i meta tags
  const getImageUrl = () => {
    if (selectedProject?.thumbnail?.url) {
      // Se l'URL è già assoluto, usalo direttamente
      if (
        selectedProject.thumbnail.url.startsWith('http://') ||
        selectedProject.thumbnail.url.startsWith('https://')
      ) {
        return selectedProject.thumbnail.url;
      }
      // Altrimenti costruisci l'URL usando Strapi API URL
      return `${process.env.REACT_APP_STRAPI_API_URL || ''}${selectedProject.thumbnail.url}`;
    }
    return `${window.location.origin}/logo.png`;
  };

  // Helper per ottenere il titolo per i meta tags
  const getPageTitle = () => {
    if (selectedProject?.titoloProgetto) {
      return `${selectedProject.titoloProgetto} - CIL - Creatività in Libertà`;
    }
    return 'Progetti in Corso - CIL - Creatività in Libertà';
  };

  // Helper per ottenere la descrizione per i meta tags
  const getPageDescription = () => {
    if (selectedProject?.descrizione) {
      return selectedProject.descrizione.substring(0, 200);
    }
    return "Scopri i progetti attualmente attivi di CIL – Creatività in Libertà. Ogni progetto nasce dall'incontro tra creatività, gioco, partecipazione e cura della comunità.";
  };

  return (
    <Box bg="#36393f" minH="100vh" py={12} color="white">
      <Helmet>
        <title>{getPageTitle()}</title>
        <meta
          property="og:title"
          content={selectedProject?.titoloProgetto || 'Progetti in Corso'}
        />
        <meta property="og:description" content={getPageDescription()} />
        <meta property="og:image" content={getImageUrl()} />
        <meta
          property="og:url"
          content={`${window.location.origin}/progetti/in-corso`}
        />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content={selectedProject?.titoloProgetto || 'Progetti in Corso'}
        />
        <meta name="twitter:description" content={getPageDescription()} />
        <meta name="twitter:image" content={getImageUrl()} />
      </Helmet>
      <Container maxW="container.xl" px={{ base: 4, md: 8 }}>
        {/* Header con titolo */}
        <VStack spacing={6} mb={16} textAlign="center">
          <Heading
            as="h1"
            fontSize={{ base: '4xl', md: '5xl', lg: '6xl' }}
            fontWeight="extrabold"
            color="white"
            mb={4}
            fontFamily="Poppins, sans-serif"
          >
            Progetti in Corso
          </Heading>

          <Text
            fontSize={{ base: 'lg', md: 'xl' }}
            color="gray.300"
            maxW="3xl"
            lineHeight="1.6"
            fontFamily="Montserrat, sans-serif"
          >
            <h1>Progetti in Corso</h1>
            <p>
              Scopri i progetti attualmente attivi di CIL – Creatività in
              Libertà. In questa sezione trovi tutte le attività che sono in
              corso all&apos;interno della nostra associazione. Ogni progetto
              nasce dall&apos;incontro tra creatività, gioco, partecipazione e
              cura della comunità, e rappresenta un tassello importante del
              nostro percorso.
            </p>
          </Text>
        </VStack>

        {/* CTA per proporre progetti */}
        <Box
          textAlign="center"
          bg="#2f3136"
          borderRadius="20px"
          p={8}
          boxShadow="xl"
          border="1px solid"
          borderColor="gray.600"
          mb={12}
        >
          <VStack spacing={6}>
            <Box
              w="20"
              h="20"
              bg="#ffcc00"
              borderRadius="full"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <Icon as={FaUsers} w={10} h={10} color="black" />
            </Box>

            <VStack spacing={3}>
              <Heading
                as="h3"
                fontSize={{ base: 'xl', md: '2xl' }}
                color="white"
                fontWeight="bold"
                fontFamily="Poppins, sans-serif"
              >
                Hai un&apos;idea per un progetto?
              </Heading>

              <Text
                fontSize={{ base: 'md', md: 'lg' }}
                color="gray.300"
                lineHeight="1.6"
                fontFamily="Montserrat, sans-serif"
                maxW="2xl"
              >
                CIL è sempre alla ricerca di nuove idee e progetti creativi! Se
                hai un&apos;idea per un&apos;attività, un laboratorio o un
                evento, contattaci e raccontaci la tua proposta.
              </Text>
            </VStack>

            <Button
              as="a"
              href="/contattaci"
              bg="#ffcc00"
              color="black"
              size="lg"
              leftIcon={<FaUsers />}
              borderRadius="12px"
              fontWeight="600"
              _hover={{
                bg: '#e6b800',
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 25px rgba(255, 204, 0, 0.3)',
              }}
              transition="all 0.3s ease"
              fontFamily="Montserrat, sans-serif"
              px={8}
              py={4}
              fontSize="lg"
            >
              Proponi il tuo progetto
            </Button>
          </VStack>
        </Box>

        {/* Griglia progetti */}
        {loading ? (
          <Box textAlign="center" py={12}>
            <Text fontSize="lg" color="gray.600">
              Caricamento progetti...
            </Text>
          </Box>
        ) : error ? (
          <Box textAlign="center" py={12}>
            <Text fontSize="lg" color="red.500">
              Errore: {error}
            </Text>
            <Button
              onClick={() => window.location.reload()}
              mt={4}
              bg="#ffcc00"
              color="gray.800"
            >
              Riprova
            </Button>
          </Box>
        ) : progetti.length === 0 ? (
          <Box textAlign="center" py={12}>
            <Text fontSize="lg" color="gray.600">
              Nessun progetto attualmente in corso.
            </Text>
          </Box>
        ) : (
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={8} mb={16}>
            {progetti.map((progetto) => (
              <Box
                key={progetto.id}
                bg="#36393f"
                borderRadius="20px"
                overflow="hidden"
                boxShadow="xl"
                transition="all 0.3s ease"
                _hover={{
                  transform: 'translateY(-4px)',
                  boxShadow: '0 20px 40px rgba(0,0,0,0.3)',
                  borderColor: '#ffcc00',
                }}
                border="1px solid"
                borderColor="gray.600"
              >
                {/* Immagine */}
                <Box h="200px" position="relative" overflow="hidden">
                  {progetto.thumbnail?.url ? (
                    <Box
                      as="img"
                      src={progetto.thumbnail.url}
                      alt={progetto.titoloProgetto}
                      w="100%"
                      h="100%"
                      objectFit="cover"
                      objectPosition="center"
                    />
                  ) : (
                    <Box
                      h="100%"
                      bg="linear-gradient(135deg, #ffcc0020, #ffcc0040)"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                    >
                      <Icon
                        as={FaUsers}
                        w={16}
                        h={16}
                        color="#ffcc00"
                        opacity={0.8}
                      />
                      {/* Placeholder per immagine futura */}
                      <Box
                        position="absolute"
                        top={4}
                        right={4}
                        bg="#ffcc00"
                        color="white"
                        px={3}
                        py={1}
                        borderRadius="full"
                        fontSize="xs"
                        fontWeight="bold"
                        textTransform="uppercase"
                      >
                        Immagine
                      </Box>
                    </Box>
                  )}
                </Box>

                {/* Contenuto */}
                <Box p={6}>
                  <VStack spacing={4} align="stretch">
                    <Heading
                      as="h3"
                      fontSize="xl"
                      fontWeight="bold"
                      color="white"
                      lineHeight="1.3"
                      fontFamily="Poppins, sans-serif"
                    >
                      {progetto.titoloProgetto}
                    </Heading>

                    <Text
                      fontSize="md"
                      color="gray.300"
                      lineHeight="1.6"
                      fontFamily="Montserrat, sans-serif"
                      noOfLines={5}
                    >
                      {progetto.descrizione}
                    </Text>

                    {/* Pulsante Scopri di più */}
                    <Button
                      onClick={() => handleProjectClick(progetto)}
                      bg="#ffcc00"
                      color="black"
                      borderRadius="12px"
                      fontWeight="600"
                      _hover={{
                        bg: '#e6b800',
                        transform: 'translateY(-2px)',
                        boxShadow: '0 8px 25px rgba(255, 204, 0, 0.3)',
                      }}
                      transition="all 0.3s ease"
                      size="md"
                      w="full"
                      fontFamily="Montserrat, sans-serif"
                    >
                      Scopri di più
                    </Button>
                  </VStack>
                </Box>
              </Box>
            ))}
          </SimpleGrid>
        )}

        {/* Messaggio finale */}
        <Box
          textAlign="center"
          bg="#2f3136"
          borderRadius="20px"
          p={8}
          boxShadow="xl"
          border="1px solid"
          borderColor="gray.600"
        >
          <VStack spacing={4}>
            <Box
              w="16"
              h="16"
              bg="#36a9e1"
              borderRadius="full"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <Icon as={FaUsers} w={8} h={8} color="white" />
            </Box>

            <Text
              fontSize={{ base: 'lg', md: 'xl' }}
              color="gray.300"
              fontWeight="medium"
              fontFamily="Montserrat, sans-serif"
            >
              Altri progetti potrebbero iniziare presto! Seguici per restare
              aggiornato.
            </Text>

            <HStack spacing={4} mt={2}>
              <Button
                as="a"
                href="/tesseramento"
                bg="#ffcc00"
                color="black"
                size="md"
                leftIcon={<FaUsers />}
                borderRadius="12px"
                fontWeight="600"
                _hover={{
                  bg: '#e6b800',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 25px rgba(255, 204, 0, 0.3)',
                }}
                transition="all 0.3s ease"
                fontFamily="Montserrat, sans-serif"
              >
                Diventa Socio
              </Button>
              <Button
                as="a"
                href="/attivita"
                variant="outline"
                borderColor="gray.600"
                color="white"
                size="md"
                leftIcon={<FaUsers />}
                borderRadius="12px"
                fontWeight="600"
                _hover={{
                  bg: 'gray.600',
                  color: 'white',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 25px rgba(0, 0, 0, 0.3)',
                }}
                transition="all 0.3s ease"
                fontFamily="Montserrat, sans-serif"
              >
                Eventi
              </Button>
            </HStack>
          </VStack>
        </Box>
      </Container>

      {/* Modal per i dettagli del progetto */}
      <Modal
        isOpen={isOpen}
        onClose={closeModal}
        size="4xl"
        scrollBehavior="inside"
      >
        <ModalOverlay />
        <ModalContent
          mx={4}
          maxH="90vh"
          mb={8}
          bg="#2f3136"
          borderRadius="20px"
          border="1px solid"
          borderColor="gray.600"
        >
          <ModalHeader bg="#ffcc00" color="black" borderTopRadius="20px">
            <HStack justify="space-between" align="center">
              <Heading size="lg" fontFamily="Poppins, sans-serif">
                {selectedProject?.titoloProgetto}
              </Heading>
              <Button
                onClick={closeModal}
                variant="ghost"
                color="black"
                _hover={{ bg: 'rgba(0,0,0,0.1)' }}
                p={2}
              >
                <Icon as={FaTimes} />
              </Button>
            </HStack>
          </ModalHeader>

          <ModalBody p={0}>
            {selectedProject && (
              <VStack spacing={0} align="stretch">
                {/* Thumbnail principale */}
                {selectedProject.thumbnail?.url && (
                  <Box position="relative" h="300px">
                    <Image
                      src={selectedProject.thumbnail.url}
                      alt={selectedProject.titoloProgetto}
                      w="100%"
                      h="100%"
                      objectFit="cover"
                    />
                  </Box>
                )}

                {/* Gallery */}
                {selectedProject.gallery &&
                  selectedProject.gallery.length > 0 && (
                    <Box p={6} bg="#36393f">
                      {/* Immagine principale con navigazione */}
                      <Box position="relative" mb={4}>
                        <Image
                          src={
                            selectedProject.gallery[currentImageIndex]?.url ||
                            selectedProject.gallery[currentImageIndex]
                          }
                          alt={`${selectedProject.titoloProgetto} - Immagine ${currentImageIndex + 1}`}
                          borderRadius="lg"
                          objectFit="cover"
                          height="400px"
                          width="100%"
                        />

                        {/* Frecce di navigazione */}
                        {selectedProject.gallery.length > 1 && (
                          <>
                            <Button
                              position="absolute"
                              left={2}
                              top="50%"
                              transform="translateY(-50%)"
                              onClick={prevImage}
                              bg="rgba(0,0,0,0.7)"
                              color="white"
                              _hover={{ bg: 'rgba(0,0,0,0.9)' }}
                              borderRadius="full"
                              size="sm"
                              minW="40px"
                              h="40px"
                            >
                              ‹
                            </Button>
                            <Button
                              position="absolute"
                              right={2}
                              top="50%"
                              transform="translateY(-50%)"
                              onClick={nextImage}
                              bg="rgba(0,0,0,0.7)"
                              color="white"
                              _hover={{ bg: 'rgba(0,0,0,0.9)' }}
                              borderRadius="full"
                              size="sm"
                              minW="40px"
                              h="40px"
                            >
                              ›
                            </Button>
                          </>
                        )}

                        {/* Indicatore posizione */}
                        {selectedProject.gallery.length > 1 && (
                          <Text
                            position="absolute"
                            bottom={2}
                            right={2}
                            bg="rgba(0,0,0,0.7)"
                            color="white"
                            px={2}
                            py={1}
                            borderRadius="md"
                            fontSize="sm"
                            fontFamily="Montserrat, sans-serif"
                          >
                            {currentImageIndex + 1} /{' '}
                            {selectedProject.gallery.length}
                          </Text>
                        )}
                      </Box>

                      {/* Thumbnails per navigazione rapida */}
                      {selectedProject.gallery.length > 1 && (
                        <SimpleGrid
                          columns={{ base: 3, md: 4, lg: 6 }}
                          spacing={2}
                        >
                          {selectedProject.gallery.map((img, index) => (
                            <Image
                              key={generateImageKey(
                                selectedProject.id,
                                img,
                                index
                              )}
                              src={img?.url || img}
                              alt={`Thumbnail ${index + 1}`}
                              borderRadius="md"
                              objectFit="cover"
                              height="80px"
                              width="100%"
                              cursor="pointer"
                              border={
                                currentImageIndex === index
                                  ? '3px solid #ffcc00'
                                  : '1px solid #e2e8f0'
                              }
                              _hover={{
                                transform: 'scale(1.05)',
                                transition: 'all 0.2s',
                                borderColor: '#ffcc00',
                              }}
                              onClick={() => goToImage(index)}
                            />
                          ))}
                        </SimpleGrid>
                      )}
                    </Box>
                  )}

                {/* Contenuto principale */}
                <Box p={6}>
                  {/* Descrizione */}
                  <Box mb={6}>
                    <Text
                      color="gray.300"
                      lineHeight="tall"
                      fontFamily="Montserrat, sans-serif"
                    >
                      {selectedProject.descrizione}
                    </Text>
                  </Box>

                  {/* Descrizione lunga se disponibile */}
                  {selectedProject.longDescription && (
                    <Box mb={6}>
                      <Text
                        color="gray.300"
                        lineHeight="tall"
                        fontFamily="Montserrat, sans-serif"
                      >
                        {selectedProject.longDescription}
                      </Text>
                    </Box>
                  )}

                  {/* Link e azioni */}
                  <VStack spacing={4} align="stretch">
                    {selectedProject.urlLink && (
                      <Button
                        as="a"
                        href={selectedProject.urlLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        bg="#ffcc00"
                        color="black"
                        leftIcon={<FaExternalLinkAlt />}
                        borderRadius="12px"
                        fontWeight="600"
                        _hover={{
                          bg: '#e6b800',
                          transform: 'translateY(-2px)',
                          boxShadow: '0 8px 25px rgba(255, 204, 0, 0.3)',
                        }}
                        transition="all 0.3s ease"
                        size="lg"
                        fontFamily="Montserrat, sans-serif"
                      >
                        Visita il progetto
                      </Button>
                    )}

                    {selectedProject.ytLink && (
                      <Button
                        as="a"
                        href={selectedProject.ytLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        bg="#ff0000"
                        color="white"
                        leftIcon={<FaYoutube />}
                        borderRadius="12px"
                        fontWeight="600"
                        _hover={{
                          bg: '#cc0000',
                          transform: 'translateY(-2px)',
                          boxShadow: '0 8px 25px rgba(255, 0, 0, 0.3)',
                        }}
                        transition="all 0.3s ease"
                        size="lg"
                        fontFamily="Montserrat, sans-serif"
                      >
                        Guarda il video
                      </Button>
                    )}
                  </VStack>

                  {/* Spazio vuoto sotto */}
                  <Box h={{ base: 8, md: 12 }} />
                </Box>
              </VStack>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default ProgettiInCorso;
